import { useEffect, useMemo, useState } from "react"
import { CheckCircle2, ChevronDown, ChevronRight, Clock, XCircle, AlertCircle, RotateCcw } from "lucide-react"
import { router, usePage } from "@inertiajs/react"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { SuiteHealthSkill } from "@/types/agent_testing"
import type { SharedProps } from "@/types/inertia"

// Selection identifier — uniquely a (skill, scenario) pair. Clicking a
// skill group selects its most relevant scenario; clicking a scenario row
// selects that exact scenario.
export type ScenarioSelection = {
  skillId: number | null
  scenarioId: number
}

type Props = {
  skills: SuiteHealthSkill[]
  selected: ScenarioSelection | null
  onSelect: (sel: ScenarioSelection) => void
  search: string
  failureOnly: boolean
  /** Run a single scenario against the current skill state. Resilient to
      scenarios with no prior run history. */
  onRerun: (skillId: number | null, scenarioId: number) => void
  /** Scenario IDs currently being run — used to disable + spin the button. */
  busyScenarioIds: Set<number>
}

// Left-side skill/scenario index. Skills with failing scenarios default open
// so the operator's first read is what's red. Selected scenario row gets
// a soft red glow (mirrors the mockup).
export function SkillSuiteList({ skills, selected, onSelect, search, failureOnly, onRerun, busyScenarioIds }: Props) {
  // Admins get extra debugging info on each row (scenario id chip).
  // Read once at the top and thread it down to SkillGroup -> row.
  const { props: sharedProps } = usePage<SharedProps>()
  const isAdmin = sharedProps.isAdmin === true
  const filteredSkills = useMemo(() => {
    const q = search.trim().toLowerCase()
    return skills.map((s) => {
      const scenarios = s.scenarios.filter((sc) => {
        // "Needs attention" = anything that isn't a terminal-good state.
        // - failed / errored      → broken
        // - never-run (null)      → unverified
        // - pending / running     → in flight (just rerunning, queued, or
        //                           working — keep visible so the operator
        //                           sees the row transition to its terminal
        //                           state, instead of it vanishing the
        //                           moment they click rerun)
        // Passing and skipped fall off the Failures tab as before.
        const passedOrSkipped =
          sc.latest_status === "passed" || sc.latest_status === "skipped"
        if (failureOnly && passedOrSkipped) return false
        if (q && !sc.name.toLowerCase().includes(q) && !sc.key.toLowerCase().includes(q)) return false
        return true
      })
      return { ...s, scenarios }
    }).filter((s) => s.scenarios.length > 0)
  }, [skills, search, failureOnly])

  if (filteredSkills.length === 0) {
    return (
      <section className="valet-panel rounded-card p-6 text-sm text-app-muted">
        {search ? "No scenarios match this search." : failureOnly ? "Nothing failing — nice." : "No scenarios attached to this agent yet."}
      </section>
    )
  }

  return (
    <section className="valet-panel overflow-hidden rounded-card xl:flex xl:h-[calc(100vh-280px)] xl:max-h-[760px] xl:flex-col">
      <div className="flex items-center justify-between border-b border-border p-5">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-app-text sm:text-xl">Test suite</h2>
            <span className="rounded-full bg-white/[.08] px-2 py-0.5 text-xs text-app-muted">
              {filteredSkills.length} group{filteredSkills.length === 1 ? "" : "s"}
            </span>
          </div>
          <p className="mt-1 text-xs text-app-muted sm:text-sm">
            Select a test group or scenario to open the testing panel.
          </p>
        </div>
      </div>

      <div className="thin-scrollbar min-w-0 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
        {/* Header row — desktop only */}
        <div className="hidden md:grid md:grid-cols-[1fr_90px_110px_36px] border-b border-border px-5 py-3 text-[10px] uppercase tracking-wider text-app-muted">
          <div>Scenario</div>
          <div>Runtime</div>
          <div>Last run</div>
          <div className="sr-only">Rerun</div>
        </div>

        <div className="divide-y divide-border">
          {filteredSkills.map((s) => (
            <SkillGroup
              key={`${s.skill_key}-${s.skill_id ?? "_"}`}
              skill={s}
              selected={selected}
              onSelect={onSelect}
              onRerun={onRerun}
              busyScenarioIds={busyScenarioIds}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

function SkillGroup({
  skill, selected, onSelect, onRerun, busyScenarioIds, isAdmin,
}: {
  skill: SuiteHealthSkill
  selected: ScenarioSelection | null
  onSelect: (sel: ScenarioSelection) => void
  onRerun: (skillId: number | null, scenarioId: number) => void
  busyScenarioIds: Set<number>
  isAdmin: boolean
}) {
  const allPassing = skill.passing_count === skill.total_count
  const hasFailures = skill.scenarios.some(
    (sc) => sc.latest_status === "failed" || sc.latest_status === "errored",
  )
  // Default-open when there are failures or when the selection is in this group.
  const containsSelected = selected != null && skill.skill_id === selected.skillId
  const [open, setOpen] = useState(hasFailures || containsSelected)
  useEffect(() => {
    if (containsSelected) setOpen(true)
  }, [containsSelected])
  const passRate = skill.total_count > 0 ? (skill.passing_count / skill.total_count) * 100 : 0
  const preferredScenario =
    skill.scenarios.find((sc) => sc.latest_status === "failed" || sc.latest_status === "errored") ||
    skill.scenarios.find((sc) => sc.latest_run_id != null) ||
    skill.scenarios[0]

  return (
    <div>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v)
          if (preferredScenario && !containsSelected) {
            if (skill.skill_id != null) {
              onSelect({ skillId: skill.skill_id, scenarioId: preferredScenario.id })
            }
          }
        }}
        className="flex w-full items-center gap-3 px-5 py-3 text-left transition hover:bg-white/[.025]"
      >
        {open
          ? <ChevronDown size={14} className="text-app-muted" />
          : <ChevronRight size={14} className="text-app-muted" />}
        <span className="font-semibold text-app-text">{skill.skill_name}</span>
        {skill.skill_key !== skill.skill_name && (
          <code className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-app-muted">
            {skill.skill_key}
          </code>
        )}
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden h-2 w-24 overflow-hidden rounded-full bg-white/[.08] sm:block">
            <div
              className={cn(
                "h-full",
                allPassing ? "bg-status-success" : hasFailures ? "bg-status-error" : "bg-status-warning",
              )}
              style={{ width: `${passRate}%` }}
            />
          </div>
          <span className={cn(
            "text-xs",
            allPassing ? "text-status-success" : hasFailures ? "text-status-error" : "text-app-muted",
          )}>
            {skill.passing_count} / {skill.total_count} passing
          </span>
        </div>
      </button>

      {open && (
        <div>
          {skill.scenarios.map((sc) => {
            const isSelected = selected?.skillId === skill.skill_id
                            && selected?.scenarioId === sc.id
            const canSelect = true
            // Per-scenario rerun no longer depends on a prior run existing
            // — the scenario-level endpoint creates a fresh TestRun every
            // time. Skill scenarios use skill routes; integration uses
            // /testing routes.
            const canRerun  = true
            // "In flight" covers BOTH this client's optimistic-busy state
            // (just clicked rerun) AND any other origin that put the
            // scenario into pending/running (apply-time auto-rerun, a
            // sibling tab, a worker pickup). Either way: spin the icon
            // and disable the button so we don't double-fire.
            const serverSideInFlight = sc.latest_status === "pending" || sc.latest_status === "running"
            const rerunBusy = busyScenarioIds.has(sc.id) || serverSideInFlight
            const handleSelect = () => {
              if (sc.integration_path) {
                router.visit(sc.integration_path, { preserveScroll: false, preserveState: false })
                return
              }

              onSelect({ skillId: skill.skill_id, scenarioId: sc.id })
            }
            return (
              <div
                key={sc.id}
                role="button"
                tabIndex={canSelect ? 0 : -1}
                aria-disabled={!canSelect}
                onClick={handleSelect}
                onKeyDown={(e) => {
                  if (!canSelect) return
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    handleSelect()
                  }
                }}
                className={cn(
                  "grid w-full items-center gap-3 px-5 py-3 text-left transition focus:outline-none",
                  "grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_90px_110px_36px]",
                  canSelect && "cursor-pointer focus-visible:ring-1 focus-visible:ring-brand-red",
                  isSelected
                    ? "border-y border-brand-redBorder bg-brand-redSoft"
                    : canSelect && "hover:bg-white/[.025]",
                  !canSelect && "cursor-not-allowed opacity-60",
                )}
              >
                <div className="flex min-w-0 items-center gap-3 md:pl-7">
                  <StatusIcon status={sc.latest_status} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-app-text">
                        {sc.name || sc.key}
                      </span>
                      {isAdmin && (
                        <span
                          className="shrink-0 rounded-chip border border-border bg-white/[.06] px-1.5 py-0.5 font-mono text-[11px] text-app-muted"
                          title="Scenario id (admin only)"
                        >
                          ID {sc.id}
                        </span>
                      )}
                    </div>
                    <code className="block truncate font-mono text-[10px] text-app-muted">
                      {sc.key}
                    </code>
                  </div>
                </div>
                <div className="hidden text-[11px] text-app-muted md:block">
                  {/* Suite health doesn't track per-scenario runtime; leave as em-dash. */}
                  —
                </div>
                <div className="text-[11px] text-app-muted">
                  {fmtCompactRelative(sc.latest_run_at)}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (canRerun && !rerunBusy) onRerun(skill.skill_id, sc.id)
                  }}
                  disabled={!canRerun || rerunBusy}
                  title={
                    !canRerun
                      ? "Skill missing — can't rerun"
                      : rerunBusy
                        ? "Rerun in flight…"
                        : "Run this scenario against the current skill state"
                  }
                  aria-label="Rerun scenario"
                  className={cn(
                    "rounded-control border border-border p-1.5 text-app-muted transition",
                    (canRerun && !rerunBusy) && "hover:border-brand-red hover:text-app-text",
                    (!canRerun || rerunBusy) && "opacity-50",
                  )}
                >
                  <RotateCcw size={14} className={cn(rerunBusy && "animate-spin")} />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function StatusIcon({ status }: { status: string | null }) {
  if (status === "passed") {
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-status-success/40 bg-status-success/10 text-status-success">
        <CheckCircle2 size={13} />
      </span>
    )
  }
  if (status === "failed") {
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-status-error/40 bg-status-error/10 text-status-error">
        <XCircle size={13} />
      </span>
    )
  }
  if (status === "errored") {
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-status-warning/40 bg-status-warning/10 text-status-warning">
        <AlertCircle size={13} />
      </span>
    )
  }
  if (status === "running" || status === "pending" || status === "queued") {
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-status-warning/40 bg-status-warning/10 text-status-warning">
        <Clock size={13} />
      </span>
    )
  }
  return (
    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-border bg-white/[.04] text-app-muted">
      <span className="text-xs">—</span>
    </span>
  )
}
